import {computed, makeObservable, observable} from "mobx";

import {LanguageViewModel} from "../../../domain/locale/models/language/language.view-model";
import {LanguageRepository} from "../../../domain/locale/services/language/language.repository";
import {BaseLinkModel} from "../../../infrastructure/models/link/base-link-model";
import {BaseRouteMetaDataModel} from "../../../infrastructure/models/route/base-route.meta-data.model";
import {SOCIAL_TAGS, WEBSITE_MODULE} from "../website-module.consts";
import {MetaTagModel} from "./meta-tag.model";
import {contractsUtils} from "../../../infrastructure/utils/contracts-utils";
import {RouterController} from "../../../core/components/router/router-controller";
import {RouteModel} from "../../../core/components/router/models/route-model";
import {HrefModel} from "../../../core/models/meta-data/href-model";
import {BaseModel} from "../../../infrastructure/models/base-model";
import {ARGS} from "../../../core/bootstrap/args";
import {DEFAULT_CULTURE_NAME, SPORT_TYPES} from "../../../core/app/consts";
import {BaseSchemaModel} from "../../../core/models/schema/base-schema-model";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {FaqPageSchemaModel} from "../../../core/models/schema/faq-page-schema-model";
import {SITE_DOMAINS} from "../../../core/consts";

export class WebsiteModulePageMetadataViewModel extends BaseModel {
  @observable _metaData: BaseRouteMetaDataModel;
  languageRepository: LanguageRepository;
  routerController: RouterController;
  device: DeviceViewModel;

  static injectables = [LanguageRepository, RouterController, DeviceViewModel];

  constructor(
    languageRepository: LanguageRepository,
    routerController: RouterController,
    device: DeviceViewModel
  ) {
    super();

    contractsUtils.requiredType(languageRepository, LanguageRepository);
    contractsUtils.requiredType(routerController, RouterController);
    contractsUtils.requiredType(device, DeviceViewModel);

    this._metaData = new BaseRouteMetaDataModel();

    makeObservable(this);

    this.languageRepository = languageRepository;
    this.routerController = routerController;
    this.device = device;
  }

  @computed get currentLinkModel() {
    return this.routerController.navigationManager.currentLinkModel;
  }

  @computed get hrefModels(): Array<HrefModel> {
    const hrefLanguages: Array<LanguageViewModel> = this.languageRepository.languages?.filter((lang: LanguageViewModel) => lang.displayName !== DEFAULT_CULTURE_NAME);
    const hrefModels: Array<HrefModel> = hrefLanguages?.map((lang: LanguageViewModel) => {
      const href = this.getHreflangURLByLanguage(this.currentLinkModel, lang);
      const hrefModel: HrefModel = new HrefModel(href, this.hrefLangName(lang));

      return hrefModel;
    });

    return hrefModels;
  }

  @computed get shouldRenderHrefModels() {
    const homepageValid = window.location.href.split('?')[0] === `${SITE_DOMAINS.full}/` || window.location.href.split('?')[0] === `${SITE_DOMAINS.full}/`
    const otherPagesValid = this.canonicalUrl === window.location.href.split('#')[0] || this.canonicalUrl === window.location.href.split('?')[0];

    return homepageValid || otherPagesValid;
  }

  @computed get currentCultureName(): String {
    return this.languageRepository.selectedLanguage?.displayName;
  }

  @computed get dir(): String {
    return this.device.isRtl ? 'rtl' : 'ltr';
  }

  @computed get canonicalUrl(): String {
    if (!this.currentLinkModel) {
      return null;
    }

    const routeModel: RouteModel = this.routerController.findRouteByName(this.currentLinkModel?.name);
    const path: String = this.routerController.navigationManager.createUrl(routeModel.config.getCanonicalPath(this.currentLinkModel?.params), this.currentLinkModel, this.shouldUseHashByRoute);
    let href: String = (`${SITE_DOMAINS.full}${this.currentCultureName === DEFAULT_CULTURE_NAME ? '' : '/' + this.currentCultureName}${path}`).replace(/\?.*$/, '');

    const lastChar = href.charAt(href.length - 1);
    if (lastChar === '/') {
      return href.substring(0, href.length - 1)
    }

    return href;
  }

  @computed get xDefault(): String {
    const routeModel: RouteModel = this.routerController.findRouteByName(this.currentLinkModel?.name);

    if (!routeModel) {
      return this.websiteMainUrl;
    }

    const path: String = this.routerController.navigationManager.createUrl(routeModel.config.getCanonicalPath(this.currentLinkModel?.params), this.currentLinkModel);
    const to: String = this.websiteMainUrl + path;

    const lastChar = to.charAt(to.length - 1);
    if (lastChar === '/') {
      return to.substring(0, to.length - 1)
    }

    return to;
  }

  @computed get metaData(): BaseRouteMetaDataModel {
    return this._metaData;
  }

  @computed get description(): String {
    return this.metaData?.description;
  }

  @computed get title(): String {
    return this.metaData?.pageTitle;
  }

  @computed get entity() {
    return this.metaData?.entity;
  }

  @computed get isNoIndex(): Boolean {
    const isHindiLanguage: Boolean = this.languageRepository.selectedLanguage.id === 216;
    const isCricket: Boolean = this.metaData?.sportType?.id === SPORT_TYPES.cricket;

    return ARGS.NO_INDEX === 'true' || this.metaData.isNoIndex || (isHindiLanguage && !isCricket);
  }

  @computed get dataSchema(): Array<BaseSchemaModel> {
    return this.metaData?.dataSchema?.removeNulls();
  }

  @computed get dataSchemaFAQ(): FaqPageSchemaModel {
    return this.metaData?.dataSchemaFAQ;
  }

  @computed get socialTags(): Array<MetaTagModel> {
    const tags: Array<MetaTagModel> = [];

    Object.values(SOCIAL_TAGS).forEach((socialTag) => {
      tags.push(new MetaTagModel(`${socialTag}:title`, this.title));
      tags.push(new MetaTagModel(`${socialTag}:description`, this.description));
      tags.push(new MetaTagModel(`${socialTag}:image`, 'https://imagecache.365scores.com/image/upload/f_png,c_limit,q_auto:eco,w_1200,h_630/v1/WebSite/AssetsSVGNewBrand/Social_share_image.png'));

      if (socialTag === SOCIAL_TAGS.og) {
        tags.push(new MetaTagModel(`${socialTag}:url`, this.canonicalUrl));
        tags.push(new MetaTagModel(`${socialTag}:type`, 'website'));
        tags.push(new MetaTagModel(`${socialTag}:site_name`, '365Scores'));
      } else if (socialTag === SOCIAL_TAGS.twitter) {
        tags.push(new MetaTagModel(`${socialTag}:card`, 'summary_large_image'));
        tags.push(new MetaTagModel(`${socialTag}:site`, '@365Scores'));
      }

      return tags;
    });

    return tags;
  }

  getHreflangURLByLanguage(linkModel: BaseLinkModel, lang: LanguageViewModel) {
    if (!this.currentLinkModel) {
      return null;
    }

    const routeModel: RouteModel = this.routerController.findRouteByName(this.currentLinkModel.name);
    const linkModelByDisplayName: BaseLinkModel = this._getCurrentLinkModelByDisplayName(linkModel, lang.displayName);
    const currentLink = this.routerController.navigationManager.createUrl(routeModel.config.path, linkModelByDisplayName);
    const href: String = `${this.websiteMainUrl}/${lang.displayName}${currentLink}`;
    const lastChar = href.charAt(href.length - 1);
    if (lastChar === '/') {
      return href.substring(0, href.length - 1)
    }

    return href;
  }

  _getCurrentLinkModelByDisplayName(linkModel, displayName): BaseLinkModel {
    const currentLinkModel: BaseLinkModel = linkModel.clone();
    currentLinkModel.cultureName = displayName;

    return currentLinkModel;
  }

  get websiteMainUrl() {
    return `${window.location.protocol}//${window.location.host}`
  }

  hrefLangName(lang): String {
    const cultureNameLanguages: Array<Number> = Object.values(WEBSITE_MODULE.HREF_CULTURE_NAME_LANG_ID)
    let displayName: String = cultureNameLanguages.includes(lang.id) ? lang.cultureName : lang.displayName;

    return displayName
  }

  @computed get shouldUseHashByRoute(): Boolean {
    const screenWithHash: Array<String> = [];

    return this.currentLinkModel.hashParams?.brackets || screenWithHash.includes(this.currentLinkModel?.name);
  }
}
